import React, { useState, useEffect, useMemo, useCallback } from 'react';
import authService from '../../api-authorization/AuthorizeService';
import axios from 'axios';
import SupportUs from '../supporters/SupportUs';
import PGNViewerWithAnalysis from '../../PGNViewerWithAnalysis';
import { Button, Box, Grid, Typography, Alert, Switch, FormControlLabel, Paper, Chip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { Link } from 'react-router-dom';
import { usePromiseTracker } from "react-promise-tracker";
import { trackPromise } from 'react-promise-tracker';
import { MagnifyingGlass } from "react-loader-spinner";
import AnalyzeOnLichess from './AnalyzeOnLichess';
import EvaluationBar from './EvaluationBar';
import { useStockfish } from '../../../hooks/useStockfish';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import { Chess } from 'chess.js';
import TopMovesPanel from './TopMovesPanel';

const extractPGNTag = (line, tagName) => {
    if (line.includes(`[${tagName} `)) {
        const match = line.match(/(?:"[^"]*"|^[^"]*$)/);
        return match ? match[0].replace(/"/g, "") : "";
    }
    return null;
};

const useWindowDimensions = () => {
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });

    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return dimensions;
};

const PlayerLink = ({ name, uscfId, fideId, elo }) => {
    const renderPlayerName = () => {
        if (uscfId > 0) {
            return (
                <a
                    href={`http://www.uschess.org/msa/MbrDtlMain.php?${uscfId}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    {name}
                </a>
            );
        }
        if (fideId > 0) {
            return (
                <a
                    href={`https://ratings.fide.com/profile/${fideId}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    {name}
                </a>
            );
        }
        return name;
    };

    return (
        <>
            {elo > 0 && `[${elo}] `}
            {renderPlayerName()}
        </>
    );
};

const EventLink = ({ eventName, uscfId, fideId, date }) => {
    const renderEventName = () => {
        if (uscfId > 0) {
            return (
                <Link target="_blank" to={`/Games/GamesByEventID?idType=USCF&id=${uscfId}`}>
                    {eventName}
                </Link>
            );
        }
        if (fideId > 0) {
            return (
                <Link target="_blank" to={`/Games/GamesByEventID?idType=FIDE&id=${fideId}`}>
                    {eventName}
                </Link>
            );
        }
        return eventName;
    };

    const renderDate = () => {
        if (fideId > 0) {
            return (
                <a
                    href={`https://ratings.fide.com/tournament_information.phtml?event=${fideId}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    [{date}]
                </a>
            );
        }
        if (uscfId > 0) {
            return (
                <a
                    href={`http://www.uschess.org/msa/XtblMain.php?${uscfId}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    [{date}]
                </a>
            );
        }
        return `[${date}]`;
    };

    return (
        <>
            {renderEventName()} {renderDate()}
        </>
    );
};

export default function GameById(props) {
    const gameId = useMemo(() =>
        props.gameId || props.match?.params?.gameId || "",
        [props.gameId, props.match?.params?.gameId]
    );

    const [gameData, setGameData] = useState({
        pgn: null,
        event: "",
        date: "",
        white: "",
        whiteElo: 0,
        whiteFideId: 0,
        whiteUscfId: 0,
        black: "",
        blackElo: 0,
        blackFideId: 0,
        blackUscfId: 0,
        result: "",
        uscfEventId: 0,
        fideEventId: 0
    });

    const [isAdmin, setIsAdmin] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [enableAnalysis, setEnableAnalysis] = useState(false);
    const [currentFen, setCurrentFen] = useState(null);
    const [boardOrientation, setBoardOrientation] = useState('white');
    const [currentPath, setCurrentPath] = useState([]);
    const [currentMoveIndex, setCurrentMoveIndex] = useState(0);
    const [variations, setVariations] = useState({});
    const [activeVariationIndex, setActiveVariationIndex] = useState(null);
    const [startingFen, setStartingFen] = useState(null);
    const [emailDialogOpen, setEmailDialogOpen] = useState(false);
    const [emailDialogText, setEmailDialogText] = useState("");

    const displayEditButton = props.displayEditButton !== undefined ? props.displayEditButton : true;
    const { promiseInProgress } = usePromiseTracker();
    const dimensions = useWindowDimensions();

    const { isReady, evaluation, bestMove, topMoves, analysisDepth, isAnalyzing, analyzePosition, stopAnalysis } = useStockfish(3);

    const parsePGNTags = useCallback((pgnLines) => {
        const tags = {
            event: "",
            date: "",
            white: "",
            black: "",
            whiteElo: 0,
            blackElo: 0,
            result: "",
            fideEventId: 0,
            uscfEventId: 0,
            whiteUscfId: 0,
            whiteFideId: 0,
            blackUscfId: 0,
            blackFideId: 0
        };

        pgnLines.forEach((line) => {
            const eventValue = extractPGNTag(line, "Event");
            if (eventValue) tags.event = eventValue;

            const dateValue = extractPGNTag(line, "Date");
            if (dateValue) tags.date = dateValue;

            const whiteValue = extractPGNTag(line, "White");
            if (whiteValue) tags.white = whiteValue;

            const blackValue = extractPGNTag(line, "Black");
            if (blackValue) tags.black = blackValue;

            const whiteEloValue = extractPGNTag(line, "WhiteElo");
            if (whiteEloValue) tags.whiteElo = parseInt(whiteEloValue) || 0;

            const blackEloValue = extractPGNTag(line, "BlackElo");
            if (blackEloValue) tags.blackElo = parseInt(blackEloValue) || 0;

            const resultValue = extractPGNTag(line, "Result");
            if (resultValue) tags.result = resultValue;

            const fideEventIdValue = extractPGNTag(line, "FideEventId");
            if (fideEventIdValue) tags.fideEventId = parseInt(fideEventIdValue) || 0;

            const uscfEventIdValue = extractPGNTag(line, "UscfEventId");
            if (uscfEventIdValue) tags.uscfEventId = parseInt(uscfEventIdValue) || 0;

            const whiteUscfIdValue = extractPGNTag(line, "WhiteUscfId");
            if (whiteUscfIdValue) tags.whiteUscfId = parseInt(whiteUscfIdValue) || 0;

            const whiteFideIdValue = extractPGNTag(line, "WhiteFideId");
            if (whiteFideIdValue) tags.whiteFideId = parseInt(whiteFideIdValue) || 0;

            const blackUscfIdValue = extractPGNTag(line, "BlackUscfId");
            if (blackUscfIdValue) tags.blackUscfId = parseInt(blackUscfIdValue) || 0;

            const blackFideIdValue = extractPGNTag(line, "BlackFideId");
            if (blackFideIdValue) tags.blackFideId = parseInt(blackFideIdValue) || 0;
        });

        return tags;
    }, []);

    useEffect(() => {
        if (!gameId) return;

        async function fetchGame() {
            try {
                const user = await authService.getUser();
                if (user?.role?.includes("Administrators")) {
                    setIsAdmin(true);
                }

                const token = await authService.getAccessToken();
                const headers = token ? { headers: { 'Authorization': `Bearer ${token}` } } : {};

                trackPromise(
                    axios.get(`/api/chessgames/getSingleGame?gamehash=${gameId}`, headers)
                        .then((response) => {
                            const pgnLines = response.data;
                            const parsedTags = parsePGNTags(pgnLines);

                            const fenLine = pgnLines.find(line => line.includes('[FEN '));
                            const customFen = fenLine ? extractPGNTag(fenLine, 'FEN') : null;
                            setStartingFen(customFen);

                            setGameData({
                                pgn: pgnLines,
                                ...parsedTags
                            });
                            setErrorMessage("");
                        })
                        .catch((error) => {
                            console.error('Error fetching game:', error);
                            setErrorMessage(error.response?.data || 'Failed to load game');
                        })
                );
            } catch (error) {
                console.error('Error in fetchGame:', error);
                setErrorMessage('An unexpected error occurred');
            }
        }

        fetchGame();
    }, [gameId, parsePGNTags]);

    useEffect(() => {
        if (enableAnalysis && currentFen && isReady) {
            analyzePosition(currentFen, 25);
        } else if (!enableAnalysis) {
            stopAnalysis();
        }
    }, [enableAnalysis, currentFen, isReady, analyzePosition, stopAnalysis]);

    const handlePositionChange = useCallback((fen, orientation) => {
        setCurrentFen(fen);
        setBoardOrientation(orientation || 'white');
    }, []);

    const handleTopMoveClick = useCallback((move) => {
        if (!move || !currentFen) return;

        const chess = new Chess(currentFen);
        const from = move.slice(0, 2);
        const to = move.slice(2, 4);
        const promotion = move.length > 4 ? move.slice(4) : undefined;

        try {
            const moveResult = chess.move({ from, to, promotion });
            if (moveResult) {
                const pgnString = gameData.pgn.join('\n');
                const gameChess = new Chess();

                if (startingFen) {
                    gameChess.load(startingFen);
                }

                gameChess.loadPgn(pgnString);
                const gameMoves = gameChess.history({ verbose: true });

                const currentGameMove = gameMoves[currentMoveIndex];

                const isDifferent = !currentGameMove ||
                    currentGameMove.from !== from ||
                    currentGameMove.to !== to;

                if (isDifferent) {
                    setCurrentPath(prev => [...prev, { move: moveResult, fen: chess.fen(), variations: {} }]);

                    if (currentPath.length === 0) {
                        const currentVars = variations[currentMoveIndex] || [];

                        const variationExists = currentVars.some(v =>
                            v.length > 0 &&
                            v[0].move.from === moveResult.from &&
                            v[0].move.to === moveResult.to
                        );

                        if (!variationExists) {
                            const newVariation = [{ move: moveResult, fen: chess.fen(), variations: {} }];
                            setVariations(prev => ({
                                ...prev,
                                [currentMoveIndex]: [...currentVars, newVariation]
                            }));
                        }
                    }
                } else {
                    setCurrentMoveIndex(currentMoveIndex + 1);
                    setCurrentPath([]);
                }
            }
        } catch (error) {
            console.error('Invalid move:', error);
        }
    }, [currentFen, gameData.pgn, currentMoveIndex, variations, currentPath, startingFen]);

    const handleFlipBoard = useCallback(() => {
        setBoardOrientation(prev => prev === 'white' ? 'black' : 'white');
    }, []);

    const boardSize = useMemo(() => {
        const headerSpace = 300;
        const sideSpace = enableAnalysis ? 440 : 380;

        const availableWidth = (dimensions.width * 0.8) - sideSpace;
        const availableHeight = (dimensions.height * 0.8) - headerSpace;

        const calculatedSize = Math.min(availableWidth, availableHeight);
        return Math.max(350, Math.min(calculatedSize, 650));
    }, [dimensions, enableAnalysis]);

    const pgnMoves = useMemo(() => {
        if (!gameData.pgn) return "";
        return gameData.pgn
            .filter(line => !line.startsWith("[") && line.trim() !== "")
            .join("\n");
    }, [gameData.pgn]);

    const buildExportPgnString = useCallback(() => {
        if (!gameData.pgn) return "";

        const chess = new Chess();
        let pgnOutput = '';

        let hasFEN = false;
        let hasSetUp = false;

        gameData.pgn.forEach(line => {
            if (line.startsWith('[')) {
                pgnOutput += line + '\n';
                if (line.includes('[FEN ')) hasFEN = true;
                if (line.includes('[SetUp ')) hasSetUp = true;
            }
        });

        if (startingFen && !hasFEN) {
            pgnOutput += `[FEN "${startingFen}"]\n`;
            if (!hasSetUp) {
                pgnOutput += `[SetUp "1"]\n`;
            }
        }

        pgnOutput += '\n';

        if (startingFen) {
            chess.load(startingFen);
        } else {
            chess.reset();
        }

        const pgnString = gameData.pgn.join('\n');
        const tempChess = new Chess();
        tempChess.loadPgn(pgnString);
        const moves = tempChess.history({ verbose: true });

        if (startingFen) {
            chess.load(startingFen);
        } else {
            chess.reset();
        }

        let moveText = '';

        const formatVariation = (variation, startIndex) => {
            let varText = '';
            variation.forEach((pathMove, idx) => {
                const moveNum = Math.floor((startIndex + idx) / 2) + 1;
                const isWhite = (startIndex + idx) % 2 === 0;

                if (isWhite) {
                    varText += `${moveNum}. `;
                } else if (idx === 0) {
                    varText += `${moveNum}... `;
                }

                varText += pathMove.move.san + ' ';
            });
            return varText.trim();
        };

        for (let i = 0; i < moves.length; i++) {
            const move = moves[i];
            const moveNumber = Math.floor(i / 2) + 1;
            const isWhiteMove = i % 2 === 0;

            if (isWhiteMove) {
                moveText += `${moveNumber}. `;
            }

            moveText += move.san + ' ';

            if (variations[i]) {
                variations[i].forEach((variation) => {
                    moveText += `(${formatVariation(variation, i)}) `;
                });
            }

            if (i === currentMoveIndex - 1 && currentPath.length > 0 && activeVariationIndex === null) {
                moveText += `(${formatVariation(currentPath, currentMoveIndex)}) `;
            }
        }

        const resultMatch = gameData.pgn.find(line => line.includes('[Result'));
        if (resultMatch) {
            const result = resultMatch.match(/"([^"]+)"/)?.[1] || '*';
            moveText += result;
        } else {
            moveText += '*';
        }

        pgnOutput += moveText;
        return pgnOutput;
    }, [gameData.pgn, currentPath, currentMoveIndex, variations, activeVariationIndex, startingFen]);

    const exportPGN = useCallback(() => {
        const pgnOutput = buildExportPgnString();
        if (!pgnOutput) return;

        const blob = new Blob([pgnOutput], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analysis_${Date.now()}.pgn`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [buildExportPgnString]);

    const emailPGN = useCallback(async () => {
        const pgnOutput = buildExportPgnString();
        if (!pgnOutput) {
            setEmailDialogText("Error: No PGN to email.");
            setEmailDialogOpen(true);
            return;
        }

        try {
            const token = await authService.getAccessToken();
            const res = await fetch("/api/chessgames/EmailAnalysis", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    pgn: pgnOutput,
                    chapterName: gameData.event || "Analysis"
                })
            });

            if (!res.ok) {
                const errorText = await res.text();
                setEmailDialogText(`Failed to send email: ${errorText || res.statusText}`);
                setEmailDialogOpen(true);
                return;
            }

            const data = await res.json();
            setEmailDialogText(data.message || "Game analysis email sent successfully!");
            setEmailDialogOpen(true);
        } catch (err) {
            setEmailDialogText(`Failed to send email: ${err.message}`);
            setEmailDialogOpen(true);
        }
    }, [buildExportPgnString, gameData.event]);

    const centerDivStyle = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '20vh'
    };

    if (promiseInProgress) {
        return (
            <Box sx={{ pt: 2, pb: 1 }}>
                <div style={centerDivStyle}>
                    <MagnifyingGlass ariaLabel="Loading" color="#00BFFF" />
                </div>
            </Box>
        );
    }

    return (
        <Box sx={{ pt: 2, pb: 1 }}>
            {gameData.pgn && (
                <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={12}>
                        <Typography variant="h6" align="center" component="div">
                            <PlayerLink
                                name={gameData.white}
                                uscfId={gameData.whiteUscfId}
                                fideId={gameData.whiteFideId}
                                elo={gameData.whiteElo}
                            />
                            {' '}
                            {gameData.result}
                            {' '}
                            <PlayerLink
                                name={gameData.black}
                                uscfId={gameData.blackUscfId}
                                fideId={gameData.blackFideId}
                                elo={gameData.blackElo}
                            />
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="subtitle1" align="center" component="div">
                            <EventLink
                                eventName={gameData.event}
                                uscfId={gameData.uscfEventId}
                                fideId={gameData.fideEventId}
                                date={gameData.date}
                            />
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                            {isAdmin && displayEditButton && (
                                <Button
                                    variant="contained"
                                    component={Link}
                                    to={`/Games/EditGameById/${gameId}`}
                                    color="primary"
                                    size="small"
                                    sx={{ height: '32px' }}
                                >
                                    Edit Data
                                </Button>
                            )}

                            <Button
                                variant="contained"
                                startIcon={<DownloadIcon />}
                                onClick={exportPGN}
                                color="primary"
                                size="small"
                                sx={{ height: '32px' }}
                            >
                                Export PGN
                            </Button>

                            <Button
                                variant="contained"
                                startIcon={<EmailIcon />}
                                onClick={emailPGN}
                                color="secondary"
                                size="small"
                                sx={{ height: '32px' }}
                            >
                                Email
                            </Button>

                            {isAdmin && (
                                <Box sx={{ display: 'flex', alignItems: 'center', height: '32px' }}>
                                    <AnalyzeOnLichess
                                        studyId="T9fzisoU"
                                        chapterName={gameData.event +" "+ gameData.white + " vs " + gameData.black}
                                        apiToken={process.env.REACT_APP_LICHESS_API_TOKEN || "lip_1bQ56NtA8QuhUJU6oVFo"}
                                        pgn={pgnMoves}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} display="flex" justifyContent="center" alignItems="flex-start" gap={2}>
                        {enableAnalysis && (
                            <Box sx={{ display: 'flex', alignItems: 'stretch', height: boardSize }}>
                                <EvaluationBar
                                    evaluation={evaluation}
                                    isAnalyzing={isAnalyzing}
                                    orientation={boardOrientation}
                                    analysisDepth={analysisDepth}
                                />
                            </Box>
                        )}
                        <Box>
                            <PGNViewerWithAnalysis
                                pgn={gameData.pgn}
                                size={boardSize}
                                onPositionChange={handlePositionChange}
                                currentPath={currentPath}
                                setCurrentPath={setCurrentPath}
                                currentMoveIndex={currentMoveIndex}
                                setCurrentMoveIndex={setCurrentMoveIndex}
                                boardOrientation={boardOrientation}
                                variations={variations}
                                setVariations={setVariations}
                                activeVariationIndex={activeVariationIndex}
                                setActiveVariationIndex={setActiveVariationIndex}
                                flipButton={
                                    <Button
                                        variant="outlined"
                                        startIcon={<FlipCameraAndroidIcon />}
                                        onClick={handleFlipBoard}
                                        size="small"
                                        sx={{ height: '32px' }}
                                    >
                                        Flip
                                    </Button>
                                }
                                analysisToggle={
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={enableAnalysis}
                                                onChange={(e) => setEnableAnalysis(e.target.checked)}
                                                disabled={!isReady}
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                        label={isReady ? "Analysis" : "Loading..."}
                                        labelPlacement="start"
                                    />
                                }
                                topMovesPanel={
                                    enableAnalysis ? (
                                        <TopMovesPanel
                                            topMoves={topMoves || []}
                                            onMoveClick={handleTopMoveClick}
                                            currentFen={currentFen}
                                        />
                                    ) : null
                                }
                            />
                        </Box>
                    </Grid>
                </Grid>
            )}

            {errorMessage && (
                <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={12}>
                        <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>
                            {errorMessage}
                        </Alert>
                    </Grid>
                    <Grid item xs={12}>
                        <SupportUs />
                    </Grid>
                </Grid>
            )}

            <Dialog open={emailDialogOpen} onClose={() => setEmailDialogOpen(false)}>
                <DialogTitle>Email PGN</DialogTitle>
                <DialogContent>
                    <DialogContentText>{emailDialogText}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailDialogOpen(false)}>OK</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}